import { MaterialGroup } from '@cutr/constants/cutlist';
import { NestingOutput, NestingProgress } from '@cutr/nesting';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { NestingGroup } from '@/utils/nesting';

export type SheetGroup = NestingOutput & NestingGroup;
export type NestingStore = {
  sheetGroups: SheetGroup[];
  setNesting(sheetGroups: SheetGroup[]): void;
  initialNestingOutputs: Record<string, NestingOutput>;
  setInitialNestingOutputs(outputs: Record<string, NestingOutput>): void;
};

export const useNestingStore = create<NestingStore>()(
  devtools((set) => ({
    sheetGroups: [],
    setNesting: (sheetGroups) => set(() => ({ sheetGroups })),
    initialNestingOutputs: {},
    setInitialNestingOutputs: (initialNestingOutputs) =>
      set(() => ({ initialNestingOutputs })),
  }))
);

const NestingStatus = {
  RUNNING: 'running',
  COMPLETED: 'completed',
} as const;

type NestingStatus = (typeof NestingStatus)[keyof typeof NestingStatus];

export type { NestingProgress };

export function computeTotalProgress(
  workers: Record<number, NestingProgress>,
  totalWorkers: number
): number {
  const sum = Object.values(workers).reduce((acc, p) => acc + p.value, 0);
  return sum / totalWorkers;
}

export type WorkerMeta = {
  lengthMM: number;
  widthMM: number;
};

export type NestingStatusStore = {
  status: Record<MaterialGroup['id'], NestingStatus>;
  progress: Record<MaterialGroup['id'], NestingProgress>;
  workerProgress: Record<MaterialGroup['id'], Record<number, NestingProgress>>;
  workerMeta: Record<MaterialGroup['id'], Record<number, WorkerMeta>>;
  setWorkerMeta(
    groupId: MaterialGroup['id'],
    workerIndex: number,
    meta: WorkerMeta
  ): void;
  setWorkerProgress(
    groupId: MaterialGroup['id'],
    workerIndex: number,
    totalWorkers: number,
    progress: NestingProgress
  ): void;
  resetProgress(): void;
  resetGroupProgress(groupId: MaterialGroup['id']): void;
  removeProgress(id: MaterialGroup['id']): void;
};

export const useNestingStatusStore = create<NestingStatusStore>()(
  devtools((set) => ({
    status: {},
    progress: {},
    workerProgress: {},
    workerMeta: {},
    setWorkerMeta: (groupId, workerIndex, meta) =>
      set((state) => ({
        workerMeta: {
          ...state.workerMeta,
          [groupId]: { ...state.workerMeta[groupId], [workerIndex]: meta },
        },
      })),
    setWorkerProgress: (groupId, workerIndex, totalWorkers, progress) =>
      set((state) => {
        const groupWorkers = {
          ...state.workerProgress[groupId],
          [workerIndex]: progress,
        };

        const totalValue = computeTotalProgress(groupWorkers, totalWorkers);
        const totalProgress: NestingProgress = {
          ...progress,
          value: totalValue,
        };

        const status =
          totalValue >= 1 ? NestingStatus.COMPLETED : NestingStatus.RUNNING;

        return {
          workerProgress: { ...state.workerProgress, [groupId]: groupWorkers },
          progress: { ...state.progress, [groupId]: totalProgress },
          status: { ...state.status, [groupId]: status },
        };
      }),
    resetGroupProgress: (groupId: MaterialGroup['id']): void =>
      set((state) => ({
        workerProgress: { ...state.workerProgress, [groupId]: {} },
        workerMeta: { ...state.workerMeta, [groupId]: {} },
        progress: {
          ...state.progress,
          [groupId]: { ...state.progress[groupId], value: 0 },
        },
        status: { ...state.status, [groupId]: NestingStatus.RUNNING },
      })),
    resetProgress: () =>
      set(() => ({
        progress: {},
        status: {},
        workerProgress: {},
        workerMeta: {},
      })),
    removeProgress: (id: MaterialGroup['id']) =>
      set((state) => {
        const newState = {
          status: { ...state.status },
          progress: { ...state.progress },
          workerProgress: { ...state.workerProgress },
          workerMeta: { ...state.workerMeta },
        };

        delete newState.progress[id];
        delete newState.status[id];
        delete newState.workerProgress[id];
        delete newState.workerMeta[id];

        return newState;
      }),
  }))
);
